import React from 'react';

import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';

import { Plan, PlanPermissions } from '@cfa/pathway-fe-shared/xp';

import useSystemPlans from '@/hooks/useSystemPlans';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';
import { cn } from '@/utils/tailwind';

import PlanCardCategoryIcon from '@/components/Cards/PlanCard/PlanCardContent/PlanCardCategoryIcon';
import { PathwayCard } from '@/components/pathway-card';

import { routes } from '@/constants';

import ArchivedPlanCardMenu from './ArchivedPlanCardMenu';
import PlanCardMenu from './PlanCardMenu';

interface ManagedPlansCardProps {
  assignedUsers: number;
  assignPlan: () => void;
  completedUsers: number;
  archivePlan: () => void;
  duplicatePlan: () => void;
  editTranslations: () => void;
  plan: Plan;
  removePlan?: () => void;
  sendPlanToParent: () => void;
}

const ManagedPlansCard: React.FC<ManagedPlansCardProps> = ({
  assignedUsers,
  assignPlan,
  completedUsers,
  archivePlan,
  duplicatePlan,
  editTranslations,
  plan,
  removePlan,
  sendPlanToParent,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { systemPlans } = useSystemPlans();
  const percentComplete = (completedUsers / assignedUsers) * 100;

  const isReadonlySubscribed = systemPlans.some(
    sp => sp.readonly && sp.id === plan.id,
  );

  const navigateToEditPlan = () => {
    navigate(routes.plans.managePlansEdit({ planId: plan.id }));
  };

  const planPermissions = Object.entries(plan.permissions ?? {}).reduce(
    (acc, [key, value]) => {
      if (value && key) {
        acc.add(key as keyof PlanPermissions);
      }
      return acc;
    },
    new Set<keyof PlanPermissions>(),
  );

  const renderPlanStatus = () => {
    if (!!assignedUsers && completedUsers === assignedUsers) {
      return (
        <PathwayCard.Subtitle tone="success" data-testid="PlanTaskCount">
          {t('Generic.completedByAll')}
        </PathwayCard.Subtitle>
      );
    }

    const teamMemberStatus = assignedUsers
      ? `${completedUsers}/${assignedUsers} ${t(
          'Generic.teamMembersCompleted',
        )}`
      : t('Generic.noTeamMembersAssigned');

    return (
      <PathwayCard.Subtitle data-testid="PlanTaskCount">
        {plan.totalSteps === 0
          ? t('Generic.noContentInPlan')
          : teamMemberStatus}
      </PathwayCard.Subtitle>
    );
  };

  const planArchived = plan.status === 'INACTIVE';
  const archiveTagClassName =
    'border-grayscale-gray3 text-variant-caption2 text-disabled-contrast! bg-disabled-base mr-4 flex items-center justify-center rounded-sm border px-2 py-0.5';

  const showProgress =
    !!assignedUsers && !!plan.totalSteps && percentComplete !== 100;

  return (
    <PathwayCard.Root
      className="animate-[fadeIn_linear_0.3s]"
      data-testid="TeamPlanCard"
      link={routes.plans.managePlan({ planId: plan.id })}
      noHover={planArchived}
    >
      <PathwayCard.Media>
        <PlanCardCategoryIcon category={plan.category} dimmed={planArchived} />
      </PathwayCard.Media>
      <PathwayCard.Body>
        <PathwayCard.Label>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PathwayCard.Label>
        <PathwayCard.Title>{itemByLanguage(plan.name)}</PathwayCard.Title>
        {renderPlanStatus()}
      </PathwayCard.Body>
      <PathwayCard.Meta className="flex-col items-start md:flex-row md:items-center">
        {planArchived && (
          <div className={cn(archiveTagClassName, 'max-md:hidden')}>
            {t('Button.archived')}
          </div>
        )}
        <p className="text-variant-body2 hidden sm:block">
          {new Date(
            plan.readonlySubscriptionSettings?.lastOptedInAt ?? plan.created,
          ).toLocaleDateString('en-US')}
        </p>
        {planArchived && (
          <div className={cn(archiveTagClassName, 'mt-1 md:hidden')}>
            {t('Button.archived')}
          </div>
        )}
      </PathwayCard.Meta>
      {showProgress && (
        <PathwayCard.Progress
          aria-label="Plan progress"
          value={percentComplete}
        />
      )}
      <PathwayCard.Actions>
        {plan.status === 'ACTIVE' ? (
          <PlanCardMenu
            assignedUsers={assignedUsers}
            assignPlan={assignPlan}
            archivePlan={archivePlan}
            duplicatePlan={duplicatePlan}
            editTranslations={editTranslations}
            isReadonly={isReadonlySubscribed}
            navigateToEditPlan={navigateToEditPlan}
            plan={plan}
            removePlan={removePlan}
            sendPlanToParent={sendPlanToParent}
            permissions={planPermissions}
          />
        ) : (
          <ArchivedPlanCardMenu plan={plan} permissions={planPermissions} />
        )}
      </PathwayCard.Actions>
    </PathwayCard.Root>
  );
};

export default ManagedPlansCard;
